// Pitch detection and note math for the tuner.
//
// Detection uses the McLeod Pitch Method (normalized square difference
// function + "key maxima" peak picking), which handles the strong overtones of
// plucked strings far better than a plain FFT peak.

// Spelled the way the site's key picker spells them.
export const NOTE_NAMES = ['C', 'C#', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'Ab', 'A', 'Bb', 'B']

const NOTE_INDEX = {
    C: 0, 'C#': 1, Db: 1, D: 2, 'D#': 3, Eb: 3, E: 4, F: 5, 'F#': 6, Gb: 6,
    G: 7, 'G#': 8, Ab: 8, A: 9, 'A#': 10, Bb: 10, B: 11,
}

// Concert pitch: the frequency of A4. Subscribers can change it (e.g. 442 Hz
// to match an orchestra); everything below takes it as an argument.
export const DEFAULT_REFERENCE_PITCH = 440
export const MIN_REFERENCE_PITCH = 410
export const MAX_REFERENCE_PITCH = 470
const A4_MIDI = 69

// Lowest/highest fundamentals we try to detect: below a 5-string bass B0 is
// pointless, and nothing we tune has a fundamental near 1.4 kHz.
const MIN_FREQUENCY = 30
const MAX_FREQUENCY = 1400

// Signals quieter than this (RMS of the raw buffer) are treated as silence.
const MIN_RMS = 0.008
// MPM: take the first key maximum within this fraction of the highest one.
const PEAK_THRESHOLD = 0.9
// Below this NSDF peak height the signal isn't periodic enough to trust.
const MIN_CLARITY = 0.8

export const frequencyToMidi = (frequency, a4 = DEFAULT_REFERENCE_PITCH) => A4_MIDI + 12 * Math.log2(frequency / a4)

export const midiToFrequency = (midi, a4 = DEFAULT_REFERENCE_PITCH) => a4 * Math.pow(2, (midi - A4_MIDI) / 12)

export const midiToNote = (midi) => ({
    name: NOTE_NAMES[((midi % 12) + 12) % 12],
    octave: Math.floor(midi / 12) - 1,
})

// "E2", "F#3", "Bb1" -> MIDI number
export const parseNote = (note) => {
    const match = /^([A-G][#b]?)(-?\d)$/.exec(note)
    if (!match || NOTE_INDEX[match[1]] === undefined) {
        return null
    }
    return NOTE_INDEX[match[1]] + (parseInt(match[2], 10) + 1) * 12
}

// For display: "F#" -> "F♯", "Bb" -> "B♭".
export const prettyNoteName = (name) => name.replace('#', '\u266f').replace(/^([A-G])b/, '$1\u266d')

export const centsBetween = (frequency, targetFrequency) => 1200 * Math.log2(frequency / targetFrequency)

// Nearest equal-tempered note to a frequency, and how far off it is.
export const describeFrequency = (frequency, a4 = DEFAULT_REFERENCE_PITCH) => {
    const exactMidi = frequencyToMidi(frequency, a4)
    const midi = Math.round(exactMidi)
    return {
        midi,
        ...midiToNote(midi),
        cents: (exactMidi - midi) * 100,
    }
}

// Averages pairs of samples: halves the work of the O(n * lag) NSDF and acts
// as a gentle low-pass filter. Fine for fundamentals well under 1.4 kHz.
const downsample = (buffer) => {
    const out = new Float32Array(buffer.length >> 1)
    for (let i = 0; i < out.length; i++) {
        out[i] = (buffer[2 * i] + buffer[2 * i + 1]) / 2
    }
    return out
}

// Returns { frequency, clarity } for the strongest periodic pitch in the
// buffer, or null if it's silent or not periodic enough.
export const detectPitch = (input, inputSampleRate) => {
    let rms = 0
    for (let i = 0; i < input.length; i++) {
        rms += input[i] * input[i]
    }
    rms = Math.sqrt(rms / input.length)
    if (rms < MIN_RMS) {
        return null
    }

    const buffer = downsample(input)
    const sampleRate = inputSampleRate / 2
    const size = buffer.length
    const minLag = Math.floor(sampleRate / MAX_FREQUENCY)
    const maxLag = Math.min(Math.ceil(sampleRate / MIN_FREQUENCY), size >> 1)

    const nsdf = new Float32Array(maxLag + 1)
    for (let lag = 0; lag <= maxLag; lag++) {
        let acf = 0
        let energy = 0
        for (let i = 0; i < size - lag; i++) {
            const a = buffer[i]
            const b = buffer[i + lag]
            acf += a * b
            energy += a * a + b * b
        }
        nsdf[lag] = energy > 0 ? (2 * acf) / energy : 0
    }

    // Key maxima: the highest point of each positive lobe after the first
    // negative-going zero crossing.
    const peaks = []
    let lag = 1
    while (lag < maxLag && nsdf[lag] > 0) {
        lag++
    }
    while (lag < maxLag) {
        while (lag < maxLag && nsdf[lag] <= 0) {
            lag++
        }
        let best = -1
        while (lag < maxLag && nsdf[lag] > 0) {
            if (lag >= minLag && (best < 0 || nsdf[lag] > nsdf[best])) {
                best = lag
            }
            lag++
        }
        if (best > 0) {
            peaks.push(best)
        }
    }
    if (peaks.length === 0) {
        return null
    }

    const highest = Math.max(...peaks.map(p => nsdf[p]))
    const chosen = peaks.find(p => nsdf[p] >= PEAK_THRESHOLD * highest)

    // Parabolic interpolation around the chosen lag for sub-sample accuracy.
    const left = nsdf[chosen - 1]
    const centre = nsdf[chosen]
    const right = nsdf[chosen + 1]
    const denominator = left - 2 * centre + right
    const shift = denominator !== 0 ? (left - right) / (2 * denominator) : 0
    const period = chosen + shift
    const clarity = centre - ((left - right) * shift) / 4

    if (clarity < MIN_CLARITY || period <= 0) {
        return null
    }
    return { frequency: sampleRate / period, clarity }
}
